import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, ArrowRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      await axios.get(`${API}/admin/me`, { withCredentials: true });
      navigate('/admin/painel');
    } catch (error) {
      // Permanece na tela de login quando ainda não autenticado.
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      toast.success('Bem-vindo ao painel admin!');
      navigate('/admin/painel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    setInitializing(true);

    try {
      const response = await axios.post(`${API}/admin/init`);
      toast.success('Sistema admin inicializado!');
      setEmail(response.data.email);
      setPassword(response.data.password);
    } catch (error) {
      if (error.response?.data?.detail === 'Admin system already initialized') {
        toast.info('Sistema já inicializado. Faça login com suas credenciais.');
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao inicializar');
      }
    } finally {
      setInitializing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F1EF] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#C8A97E] mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
            Painel Admin
          </h1>
          <p className="text-[#333333] mt-2">Virei Noiva - Área Administrativa</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-[#E8B7B1]/35">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-bold text-[#333333] uppercase tracking-wider mb-2 block">
                E-mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="admin@casorio.com"
                  className="pl-12 h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C] placeholder:text-[#333333] focus:border-[#C8A97E] focus:ring-[#C8A97E]"
                  required
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-bold text-[#333333] uppercase tracking-wider mb-2 block">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  className="pl-12 h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C] placeholder:text-[#333333] focus:border-[#C8A97E] focus:ring-[#C8A97E]"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full font-bold tracking-wider transition-colors duration-300"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Entrando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Entrar <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E8B7B1]/35">
            <p className="text-xs text-[#333333] text-center mb-3">
              Primeiro acesso? Inicialize o sistema admin.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleInitialize}
              disabled={initializing}
              className="w-full h-10 border-[#E8B7B1]/35 text-[#333333] hover:bg-[#F6F1EF] hover:text-[#1C1C1C] rounded-full text-sm"
              data-testid="admin-init-btn"
            >
              {initializing ? 'Inicializando...' : 'Inicializar Sistema Admin'}
            </Button>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[#333333] hover:text-[#C8A97E] text-sm">
            <BrandLogo size="sm" withText={false} iconClassName="h-4 w-4" />
            Voltar ao site principal
          </Link>
        </div>
      </div>
    </div>
  );
}
